import axios from 'axios';
import React, { useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { RiPencilLine, RiDeleteBinLine, RiAddLine } from 'react-icons/ri';
import styled from 'styled-components';
import { useCurrentProfile } from '../../hooks/useProfile';
import useOidcClients, { OidcClient } from '../../hooks/useOidcClients';
import { API_ENDPOINT } from '../../utils/constants';
import { fadeIn } from '../../styles/animations';
import Button from '../components/Button';
import Flex from '../components/Flex';
import Modal from '../components/Modal';
import { Table, TBody, TD, TH, THead, TR } from '../components/Table';
import Toast from '../components/Toast';
import { Heading3 } from '../components/Typography';
import Input from '../components/Input';

type FormMode = 'create' | 'edit';

const emptyForm = {
  client_name: '',
  client_id: '',
  client_secret: '',
  token_endpoint_auth_method: 'client_secret_post',
  redirect_uris: '',
  grant_types: '',
  response_types: '',
  scopes: '',
  client_uri: '',
  logo_uri: '',
};

const OidcClients = () => {
  const { data: currentProfile } = useCurrentProfile();
  const queryClient = useQueryClient();

  const { isLoading, isSuccess, data: clients, isError } = useOidcClients();

  const [showModal, setShowModal] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [editingClient, setEditingClient] = useState<OidcClient | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastSuccess, setToastSuccess] = useState(false);
  const [toastText, setToastText] = useState('');

  const isSuperAdmin = useMemo(
    () => currentProfile?.user_role === 'superadmin',
    [currentProfile?.user_role]
  );

  const openCreateModal = () => {
    setFormMode('create');
    setEditingClient(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (client: OidcClient) => {
    setFormMode('edit');
    setEditingClient(client);
    setForm({
      client_name: client.client_name || '',
      client_id: client.client_id || '',
      client_secret: '',
      token_endpoint_auth_method:
        client.token_endpoint_auth_method || 'client_secret_post',
      redirect_uris: Array.isArray(client.redirect_uris)
        ? client.redirect_uris.join(', ')
        : client.redirect_uris || '',
      grant_types: Array.isArray(client.grant_types)
        ? client.grant_types.join(', ')
        : client.grant_types || '',
      response_types: Array.isArray(client.response_types)
        ? client.response_types.join(', ')
        : client.response_types || '',
      scopes: Array.isArray(client.scopes)
        ? client.scopes.join(', ')
        : client.scopes || '',
      client_uri: client.client_uri || '',
      logo_uri: client.logo_uri || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClient(null);
    setForm(emptyForm);
  };

  const setToast = (success: boolean, message: string) => {
    setToastSuccess(success);
    setToastText(message);
    setShowToast(true);
  };

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isSuperAdmin) return;

    const payload: any = {
      client_name: form.client_name || undefined,
      client_id: form.client_id || undefined,
      client_secret: form.client_secret || undefined,
      token_endpoint_auth_method:
        form.token_endpoint_auth_method || 'client_secret_post',
      redirect_uris: form.redirect_uris
        ? form.redirect_uris
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : undefined,
      grant_types: form.grant_types
        ? form.grant_types
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : undefined,
      response_types: form.response_types
        ? form.response_types
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : undefined,
      scopes: form.scopes
        ? form.scopes
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : undefined,
      client_uri: form.client_uri || undefined,
      logo_uri: form.logo_uri || undefined,
    };

    try {
      setIsSubmitting(true);
      if (formMode === 'create') {
        const { data } = await axios.post(
          `${API_ENDPOINT}/oidc/clients`,
          payload
        );
        const createdSecret = data?.data?.client_secret;
        setToast(
          true,
          createdSecret
            ? `OIDC client created. Save this client secret now: ${createdSecret}`
            : 'OIDC client created successfully.'
        );
      } else if (editingClient) {
        await axios.put(
          `${API_ENDPOINT}/oidc/clients/${editingClient.client_id || editingClient.id}`,
          payload
        );
        setToast(true, 'OIDC client updated successfully.');
      }
      closeModal();
      queryClient.invalidateQueries(['oidc_clients']);
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        'Something went wrong while saving the client.';
      setToast(false, apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (client: OidcClient) => {
    if (!isSuperAdmin) return;
    const id = client.client_id || client.id;
    if (!id) return;

    try {
      setIsSubmitting(true);
      await axios.delete(`${API_ENDPOINT}/oidc/clients/${id}`);
      setToast(true, 'OIDC client deleted successfully.');
      queryClient.invalidateQueries(['oidc_clients']);
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        'Something went wrong while deleting the client.';
      setToast(false, apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (!isSuperAdmin) {
      return (
        <ForbiddenContainer column align="center" justify="center">
          <Heading3>Access restricted</Heading3>
          <ForbiddenText>
            This section is only available to superadmin users.
          </ForbiddenText>
        </ForbiddenContainer>
      );
    }

    if (isLoading) {
      return (
        <TR>
          <TD colSpan={7} centered>
            Loading OIDC clients...
          </TD>
        </TR>
      );
    }

    if (isError) {
      return (
        <TR>
          <TD colSpan={7} centered>
            Failed to load OIDC clients.
          </TD>
        </TR>
      );
    }

    if (isSuccess && (!clients || clients.length === 0)) {
      return (
        <TR>
          <TD colSpan={7} centered>
            No OIDC clients found. Create one to get started.
          </TD>
        </TR>
      );
    }

    return (
      clients &&
      clients.map((client, index) => (
        <StyledTR key={client.client_id || client.id || index}>
          <TD maxWidth="10%" style={{ paddingLeft: '4rem' }}>
            {index + 1}
          </TD>
          <TD>{client.client_id || client.id || '-'}</TD>
          <TD>{client.client_name || '-'}</TD>
          <TD>{client.token_endpoint_auth_method || '-'}</TD>
          <TD>
            {Array.isArray(client.redirect_uris)
              ? client.redirect_uris.join(', ')
              : client.redirect_uris || '-'}
          </TD>
          <TD>
            {Array.isArray(client.scopes)
              ? client.scopes.join(', ')
              : client.scopes || '-'}
          </TD>
          <TD>
            <ActionsContainer>
              <IconButton
                type="button"
                onClick={() => openEditModal(client)}
                disabled={!isSuperAdmin}
              >
                <RiPencilLine />
              </IconButton>
              <IconButton
                type="button"
                onClick={() => handleDelete(client)}
                disabled={!isSuperAdmin || isSubmitting}
              >
                <RiDeleteBinLine />
              </IconButton>
            </ActionsContainer>
          </TD>
        </StyledTR>
      ))
    );
  };

  return (
    <Container column>
      <Header align="center" justify="space-between">
        <Heading3>OIDC Clients</Heading3>
        {isSuperAdmin && (
          <AddClientButton onClick={openCreateModal}>
            <RiAddLine style={{ marginRight: '0.5rem' }} />
            Add Client
          </AddClientButton>
        )}
      </Header>
      <Table>
        <THead>
          <StyledTR>
            <TH style={{ paddingLeft: '4rem' }}>S.No.</TH>
            <TH>Client ID</TH>
            <TH>Client Name</TH>
            <TH>Auth Method</TH>
            <TH>Redirect URIs</TH>
            <TH>Scopes</TH>
            <TH>Actions</TH>
          </StyledTR>
        </THead>
        <TBody>{renderContent()}</TBody>
      </Table>

      {showModal && (
        <Modal>
          <FormContainer column as="form" onSubmit={handleSubmit}>
            <Heading3>
              {formMode === 'create' ? 'Add OIDC Client' : 'Edit OIDC Client'}
            </Heading3>
            <FormFields column>
              <Input
                label="Client Name"
                placeholder="My OIDC Client"
                value={form.client_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('client_name', e.target.value)
                }
                required
              />
              <Input
                label="Client ID (optional)"
                placeholder="Leave blank to auto-generate"
                value={form.client_id}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('client_id', e.target.value)
                }
              />
              <Input
                label="Client Secret (optional)"
                placeholder="Leave blank to auto-generate (if auth method is not 'none')"
                value={form.client_secret}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('client_secret', e.target.value)
                }
              />
              <SelectRow>
                <SelectLabel>Token Endpoint Auth Method</SelectLabel>
                <SelectElement
                  value={form.token_endpoint_auth_method}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleChange(
                      'token_endpoint_auth_method',
                      e.target.value
                    )
                  }
                >
                  <option value="client_secret_post">client_secret_post</option>
                  <option value="client_secret_basic">
                    client_secret_basic
                  </option>
                  <option value="none">none</option>
                </SelectElement>
              </SelectRow>
              <Input
                label="Redirect URIs (comma separated)"
                placeholder="https://app.example.com/callback, https://app.example.com/alt-callback"
                value={form.redirect_uris}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('redirect_uris', e.target.value)
                }
              />
              <Input
                label="Grant Types (comma separated)"
                placeholder="authorization_code, refresh_token"
                value={form.grant_types}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('grant_types', e.target.value)
                }
              />
              <Input
                label="Response Types (comma separated)"
                placeholder="code, token, id_token"
                value={form.response_types}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('response_types', e.target.value)
                }
              />
              <Input
                label="Scopes (comma separated)"
                placeholder="openid, profile, email"
                value={form.scopes}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('scopes', e.target.value)
                }
              />
              <Input
                label="Client URI (optional)"
                placeholder="https://example.com"
                value={form.client_uri}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('client_uri', e.target.value)
                }
              />
              <Input
                label="Logo URI (optional)"
                placeholder="https://example.com/logo.png"
                value={form.logo_uri}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('logo_uri', e.target.value)
                }
              />
            </FormFields>
            <FormActions justify="flex-end">
              <Button
                type="button"
                variant="secondary"
                onClick={closeModal}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <PrimaryButton type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? formMode === 'create'
                    ? 'Creating...'
                    : 'Saving...'
                  : formMode === 'create'
                  ? 'Create'
                  : 'Save'}
              </PrimaryButton>
            </FormActions>
          </FormContainer>
        </Modal>
      )}

      {showToast && (
        <Toast
          success={toastSuccess}
          onClose={(visible: boolean) => setShowToast(visible)}
        >
          {toastText}
        </Toast>
      )}
    </Container>
  );
};

export default OidcClients;

const Container = styled(Flex)`
  width: 100%;
  animation: ${fadeIn} 0.4s;
`;

const Header = styled(Flex)`
  width: 100%;
  padding: 1rem 4rem;
  border-top: 1px solid ${(props) => props.theme.colors.grey[200]};
  border-bottom: 1px ridge ${(props) => props.theme.colors.grey[200]};
  background-color: #fff;
`;

const StyledTR = styled(TR)`
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[200]};
`;

const AddClientButton = styled(Button)`
  width: 18rem;
  height: 4rem;
`;

const ActionsContainer = styled(Flex)`
  gap: 1rem;
`;

const IconButton = styled.button`
  outline: none;
  border: none;
  height: fit-content;
  padding: 0.5rem;
  padding-bottom: 0;
  border-radius: 4px;
  background: none;
  font-size: 2rem;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.theme.colors.grey[100]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormContainer = styled(Flex)`
  width: 42rem;
  max-height: 80vh;
  padding: 2.4rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 0.4rem 1.2rem rgba(0, 0, 0, 0.08);
  gap: 1.6rem;
  overflow-y: auto;
`;

const FormFields = styled(Flex)`
  width: 100%;
  gap: 1.5rem;
`;

const FormActions = styled(Flex)`
  width: 100%;
  gap: 1rem;
`;

const PrimaryButton = styled(Button)`
  width: 10rem;
  height: 4rem;
`;

const ForbiddenContainer = styled(Flex)`
  width: 100%;
  height: calc(100vh - 7rem);
  animation: ${fadeIn} 0.4s;
`;

const ForbiddenText = styled.p`
  margin-top: 1rem;
  font-size: 1.4rem;
  color: ${(props) => props.theme.colors.grey[400]};
`;

const SelectRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SelectLabel = styled.label`
  font-size: 1.3rem;
  font-weight: 500;
  color: ${(props) => props.theme.colors.grey[500]};
`;

const SelectElement = styled.select`
  height: 4rem;
  padding: 0 1.2rem;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.grey[200]};
  font-size: 1.4rem;
  font-family: 'Poppins';
  outline: none;

  &:focus {
    border-color: ${(props) => props.theme.colors.blue[400]};
  }
`;




